/**
 * Upload intake media (audio, image, document) via S3 presigned PUT,
 * then create a draft story task (transcribe audio / extract document text).
 */
import api from '@/lib/api';

const DOC_IMAGE_EXT = new Set([
  'png',
  'jpg',
  'jpeg',
  'webp',
  'gif',
  'bmp',
  'pdf',
  'txt',
  'md',
  'rtf',
  'csv',
  'doc',
  'docx',
  'ppt',
  'pptx',
  'xls',
  'xlsx',
]);

const AUDIO_EXT = new Set(['mp3', 'wav', 'm4a', 'aac', 'flac', 'ogg', 'opus', 'mpeg', 'mpga']);

const VIDEO_EXT = new Set(['mp4', 'mov', 'mkv', 'avi', 'm4v', 'mpeg', 'mpg', 'webm']);

function fileExt(name) {
  return String(name || '')
    .split('.')
    .pop()
    ?.toLowerCase() || '';
}

export function isAllowedIntakeFile(file) {
  if (!file?.name && !(file instanceof Blob)) return false;
  const ext = fileExt(file?.name);
  if (DOC_IMAGE_EXT.has(ext) || AUDIO_EXT.has(ext) || VIDEO_EXT.has(ext)) return true;
  const t = (file.type || '').toLowerCase();
  return (
    t.startsWith('image/') ||
    t.startsWith('audio/') ||
    t.startsWith('video/') ||
    t.startsWith('application/pdf') ||
    t.startsWith('text/')
  );
}

export function isAllowedIntakeAudio(file) {
  if (!file?.name && !(file instanceof Blob)) return false;
  if (file?.type?.startsWith?.('audio/') || file?.type === 'video/webm') return true;
  const ext = fileExt(file?.name);
  return AUDIO_EXT.has(ext) || ext === 'webm' || !file?.name; // recorded blobs may lack name
}

export function intakeFileKind(contentType, filename) {
  const t = (contentType || '').toLowerCase();
  const name = (filename || '').toLowerCase();
  const ext = fileExt(name);
  if (t.startsWith('audio/')) return 'audio';
  // Recorded voice memos often use video/webm containers
  if (t === 'video/webm' || (ext === 'webm' && !t.startsWith('video/'))) return 'audio';
  if (t.startsWith('video/') || VIDEO_EXT.has(ext)) return 'video';
  if (AUDIO_EXT.has(ext)) return 'audio';
  if (t.startsWith('image/') || /\.(png|jpe?g|webp|gif|bmp)$/.test(name)) return 'image';
  if (
    t.includes('pdf') ||
    t.includes('word') ||
    t.includes('document') ||
    t.includes('presentation') ||
    t.includes('sheet') ||
    /\.(docx?|pptx?|xlsx?|rtf|txt|md|csv|pdf)$/.test(name)
  ) {
    return 'document';
  }
  return 'file';
}

/** Accept string for multi-attachment file picker (video / image / audio / docs). */
export const INTAKE_ATTACHMENT_ACCEPT =
  'video/*,image/*,audio/*,.mp3,.wav,.m4a,.webm,.ogg,.mp4,.mov,.mkv,.avi,.pdf,.doc,.docx,.txt,.md,.rtf,.csv,.ppt,.pptx,.xls,.xlsx,application/pdf,text/*';

/**
 * @param {File|Blob} file
 * @param {{ source?: 'record'|'mp3'|'file', filename?: string, title?: string, processMedia?: boolean }} [opts]
 */
export async function uploadIntakeMediaAndCreateStoryTask(file, opts = {}) {
  if (!file) throw new Error('No file selected');
  const source = opts.source || 'file';
  const filename =
    opts.filename ||
    file.name ||
    (source === 'record' ? `voice-memo-${Date.now()}.webm` : `upload-${Date.now()}`);
  const contentType =
    file.type ||
    (source === 'record' || source === 'mp3' ? 'audio/webm' : 'application/octet-stream');

  if ((source === 'file' || source === 'video') && !isAllowedIntakeFile(file)) {
    throw new Error('Upload a video, image, document, or audio file');
  }
  if ((source === 'mp3' || source === 'record') && file instanceof File && !isAllowedIntakeAudio(file)) {
    throw new Error('Upload an audio file (MP3, WAV, M4A, WEBM, …)');
  }

  const { data: presigned } = await api.post('/media/presigned-url', null, {
    params: { filename, content_type: contentType },
  });
  const uploadUrl = presigned.upload_url || presigned.presigned_url;
  const s3Key = presigned.s3_key;
  const viewUrl = presigned.view_url || presigned.object_url || presigned.s3_url;
  if (!uploadUrl || !s3Key) throw new Error('Could not get upload URL');

  const putRes = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': contentType },
  });
  if (!putRes.ok) {
    throw new Error(`S3 upload failed (${putRes.status})`);
  }

  const { data } = await api.post('/stories/from-intake-file', {
    s3_key: s3Key,
    s3_url: viewUrl,
    filename,
    content_type: contentType,
    title: opts.title || filename.replace(/\.[^.]+$/, '') || 'Uploaded reference',
    source,
    process_media: opts.processMedia !== false,
  });
  return data;
}

/** @deprecated Prefer uploadIntakeMediaAndCreateStoryTask */
export async function uploadIntakeFileAndCreateStoryTask(file) {
  return uploadIntakeMediaAndCreateStoryTask(file, { source: 'file' });
}
